import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { X, ZoomIn, Image as ImageIcon } from 'lucide-react';
import api from '@/utils/api';

export default function GalleryPage() {
  const [settings, setSettings] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const images = (settings?.gallery_images || []).filter(img => img.url);

  return (
    <WebsiteLayout>
      <Helmet>
        <title>{`Gallery | ${settings?.company_name || 'Ganesa Homestate Developers'}`}</title>
        <meta name="description" content={`Photo gallery of ${settings?.company_name || 'Ganesa Homestate Developers'} - View our projects, properties, and happy customers.`} />
      </Helmet>

      <section className="bg-[#0A0A0F] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <span className="text-[#DAA520] text-sm font-semibold uppercase tracking-wider">Gallery</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3">Our <span className="text-[#DAA520]">Gallery</span></h1>
          <p className="text-slate-400 mt-3 max-w-2xl">Explore our projects, properties, and the trust of our happy customers.</p>
        </div>
      </section>

      <section className="py-12 sm:py-20" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {images.length > 0 ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
              {images.map((img, i) => (
                <div key={i} className="break-inside-avoid group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer border border-slate-100"
                  onClick={() => setLightbox(i)} data-testid={`gallery-image-${i}`}>
                  <img src={img.url} alt={img.caption || `Gallery ${i + 1}`} className="w-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    {img.caption && <p className="text-white text-sm font-medium">{img.caption}</p>}
                    <ZoomIn size={24} className="absolute top-4 right-4 text-white drop-shadow" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-slate-50 rounded-2xl">
              <ImageIcon size={56} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 text-lg">Gallery coming soon</p>
              <p className="text-slate-400 text-sm mt-2">Check back later for photos of our projects and properties.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && images[lightbox] && (
        <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10">
            <X size={24} />
          </button>
          {lightbox > 0 && (
            <button onClick={e => { e.stopPropagation(); setLightbox(lightbox - 1); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors text-2xl">
              &#8249;
            </button>
          )}
          {lightbox < images.length - 1 && (
            <button onClick={e => { e.stopPropagation(); setLightbox(lightbox + 1); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors text-2xl">
              &#8250;
            </button>
          )}
          <div className="max-w-5xl max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <img src={images[lightbox].url} alt={images[lightbox].caption || ''} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            {images[lightbox].caption && (
              <p className="text-white text-center mt-4 text-sm">{images[lightbox].caption}</p>
            )}
          </div>
        </div>
      )}
    </WebsiteLayout>
  );
}
